import { useState, useEffect } from "react";
import { newsStore, type NewsItem } from "../utils/dataStore";
import { apiPut } from "../../../api/http";
import { newsToApiBody } from "../../../api/loadDataStore";
import { apiDelete } from "../../../api/http";
import { reportError } from "../../../api/errors";

/** 未到定时时刻、会员端尚不可见（含历史 status=scheduled 数据） */
function isScheduledPending(n: NewsItem): boolean {
  if (n.status === "scheduled") return true;
  if (n.status !== "published" || !n.scheduledAt?.trim()) return false;
  const t = Date.parse(n.scheduledAt);
  return !Number.isNaN(t) && t > Date.now();
}

const statusConfig: Record<string, { label: string; className: string }> = {
  published: { label: "已发布", className: "bg-green-50 text-green-600" },
  draft: { label: "草稿", className: "bg-gray-100 text-gray-500" },
  pending: { label: "待审核", className: "bg-amber-50 text-amber-600" },
  unpublished: { label: "已下架", className: "bg-red-50 text-red-500" },
  scheduled: { label: "定时发布", className: "bg-teal-50 text-teal-600" },
};

interface Props {
  onPublishNew: () => void;
  onEdit?: (item: NewsItem) => void;
  tenantLegacyId: string;
  reload: () => Promise<void>;
  storeVersion: number;
}

export default function NewsList({ onPublishNew, onEdit, tenantLegacyId, reload, storeVersion }: Props) {
  const [items, setItems] = useState<NewsItem[]>(() => newsStore.getAll());
  const [filter, setFilter] = useState("全部");

  useEffect(() => {
    setItems(newsStore.getAll());
  }, [storeVersion]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [shareItem, setShareItem] = useState<NewsItem | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [statsItem, setStatsItem] = useState<NewsItem | null>(null);

  const filters = ["全部", "已发布", "草稿", "待审核", "定时发布", "已下架"];
  const filterMap: Record<string, string> = {
    已发布: "published",
    草稿: "draft",
    待审核: "pending",
    已下架: "unpublished",
    定时发布: "scheduled",
  };

  const filtered =
    filter === "全部"
      ? items.filter((n) => n.status !== "pending")
      : filter === "已发布"
        ? items.filter((n) => n.status === "published" && !isScheduledPending(n))
        : filter === "定时发布"
          ? items.filter(isScheduledPending)
          : items.filter((n) => n.status === filterMap[filter]);

  const handleDeleteConfirm = async (id: string) => {
    try {
      await apiDelete(`/api/tenants/${tenantLegacyId}/news/${encodeURIComponent(id)}`);
      await reload();
    } catch (e) {
      reportError(e, "删除失败");
    }
    setDeleteConfirmId(null);
  };

  const handleUnpublish = async (id: string) => {
    const n = items.find((x) => x.id === id);
    if (!n) return;
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/news/${encodeURIComponent(id)}`,
        newsToApiBody(n, { status: "unpublished", scheduledAt: undefined }),
      );
      await reload();
    } catch (e) {
      reportError(e, "操作失败");
    }
  };

  const handleRepublish = async (id: string) => {
    const n = items.find((x) => x.id === id);
    if (!n) return;
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/news/${encodeURIComponent(id)}`, newsToApiBody(n, { status: "published" }));
      await reload();
    } catch (e) {
      reportError(e, "操作失败");
    }
  };

  const deleteTarget = deleteConfirmId ? items.find((n) => n.id === deleteConfirmId) : null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-bold text-gray-900">资讯列表</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            共 {filtered.length} 篇；定时文章未到点不会在会员端展示
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                  filter === f ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            onClick={onPublishNew}
            className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-xs font-medium rounded-xl cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            发布新资讯
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {["标题", "分类", "作者", "阅读量", "状态", "操作"].map((h) => (
                <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((n) => {
              const listStatusKey = isScheduledPending(n) ? "scheduled" : n.status;
              const sc = statusConfig[listStatusKey] || statusConfig.draft;
              const isPublished = n.status === "published" || n.status === "scheduled";
              const isUnpublished = n.status === "unpublished";
              const hasReads = (n.readCount ?? 0) > 0;

              return (
                <tr key={n.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-4 max-w-xs">
                    <p className="font-medium text-gray-900 line-clamp-1">{n.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{n.publishDate}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full">
                      {n.category}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-gray-500">{n.author}</td>
                  <td className="px-5 py-4 text-gray-500">
                    {(n.readCount ?? 0) > 0 ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line text-xs"></i>
                        {n.readCount!.toLocaleString()}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${sc.className}`}
                    >
                      {sc.label}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      {/* Edit */}
                      <button
                        onClick={() => onEdit?.(n)}
                        title="编辑"
                        className="p-1.5 text-gray-400 hover:text-amber-500 cursor-pointer transition-colors rounded-lg hover:bg-amber-50"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>

                      {/* Stats */}
                      {(n.readCount ?? 0) > 0 && (
                        <button
                          onClick={() => setStatsItem(n)}
                          title="阅读数据统计"
                          className="p-1.5 text-gray-400 hover:text-teal-600 cursor-pointer transition-colors rounded-lg hover:bg-teal-50"
                        >
                          <i className="ri-bar-chart-2-line text-sm"></i>
                        </button>
                      )}

                      {/* Share to WeChat */}
                      <button
                        onClick={() => setShareItem(n)}
                        title="分享至微信"
                        className="p-1.5 text-gray-400 hover:text-green-600 cursor-pointer transition-colors rounded-lg hover:bg-green-50"
                      >
                        <i className="ri-wechat-fill text-sm"></i>
                      </button>

                      {/* Unpublish */}
                      {isPublished && (
                        <button
                          onClick={() => void handleUnpublish(n.id)}
                          title="下架"
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-50 text-amber-600 hover:bg-amber-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-eye-off-line text-xs"></i>下架
                        </button>
                      )}

                      {/* Republish */}
                      {isUnpublished && (
                        <button
                          onClick={() => void handleRepublish(n.id)}
                          title="重新发布"
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-600 hover:bg-green-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-send-plane-line text-xs"></i>重新发布
                        </button>
                      )}

                      {/* Delete */}
                      {hasReads && !isUnpublished ? (
                        <div
                          title="有阅读记录，请先下架再删除"
                          className="p-1.5 text-gray-200 rounded-lg cursor-not-allowed"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(n.id)}
                          title={hasReads ? "已下架，可删除" : "删除"}
                          className="p-1.5 text-gray-400 hover:text-red-500 cursor-pointer transition-colors rounded-lg hover:bg-red-50"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      )}
                    </div>
                    {hasReads && !isUnpublished && (
                      <p className="text-[10px] text-amber-500 mt-0.5">有阅读记录，请先下架</p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="py-12 text-center">
          <i className="ri-file-list-3-line text-3xl text-gray-200 mb-2 block"></i>
          <p className="text-sm text-gray-400">暂无该状态的资讯</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">确认删除资讯</h3>
            <p className="text-sm text-gray-500 text-center mb-1 leading-relaxed line-clamp-2 px-2">
              &ldquo;{deleteTarget.title}&rdquo;
            </p>
            <p className="text-xs text-gray-400 text-center mb-5">删除后无法恢复，请确认操作。</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => void handleDeleteConfirm(deleteConfirmId)}
                className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">分享至微信用户</h3>
              <button onClick={() => { setShareItem(null); setShareCopied(false); }} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Share card preview */}
            <div className="border border-gray-100 rounded-xl p-3 mb-4 flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                {shareItem.coverUrl ? (
                  <img src={shareItem.coverUrl} alt="" className="w-full h-full object-cover object-top" />
                ) : (
                  <i className="ri-newspaper-line text-gray-400 text-xl"></i>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug">{shareItem.title}</p>
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <i className="ri-article-line text-gray-400" />
                  协会资讯
                </p>
              </div>
            </div>

            {/* Share options */}
            <div className="flex gap-3 mb-4">
              <button
                className="flex-1 flex flex-col items-center gap-2 py-3 bg-green-50 rounded-xl hover:bg-green-100 cursor-pointer transition-colors"
              >
                <i className="ri-wechat-fill text-2xl" style={{ color: "#07C160" }}></i>
                <span className="text-xs text-gray-600">发送给好友</span>
              </button>
              <button
                className="flex-1 flex flex-col items-center gap-2 py-3 bg-green-50 rounded-xl hover:bg-green-100 cursor-pointer transition-colors"
              >
                <i className="ri-wechat-2-fill text-2xl" style={{ color: "#07C160" }}></i>
                <span className="text-xs text-gray-600">分享到朋友圈</span>
              </button>
              <button
                onClick={() => { setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }}
                className={`flex-1 flex flex-col items-center gap-2 py-3 rounded-xl cursor-pointer transition-all ${shareCopied ? "bg-green-500" : "bg-gray-50 hover:bg-gray-100"}`}
              >
                <i className={`${shareCopied ? "ri-check-line text-white" : "ri-link text-gray-500"} text-2xl`}></i>
                <span className={`text-xs ${shareCopied ? "text-white" : "text-gray-600"}`}>{shareCopied ? "已复制" : "复制链接"}</span>
              </button>
            </div>

            <div className="bg-amber-50 rounded-xl px-4 py-3 text-xs text-amber-700 flex items-start gap-2">
              <i className="ri-information-line text-amber-500 flex-shrink-0 mt-0.5"></i>
              <p>分享功能需与微信公众号或小程序平台集成后完整生效，当前为预览效果。</p>
            </div>
          </div>
        </div>
      )}

      {/* Reading Stats Modal — 仅展示后台累计阅读量 */}
      {statsItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl w-[480px] max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-teal-50 rounded-xl flex items-center justify-center">
                  <i className="ri-eye-line text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">阅读数据</h3>
                  <p className="text-xs text-gray-400 truncate max-w-xs">{statsItem.title}</p>
                </div>
              </div>
              <button
                onClick={() => setStatsItem(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              <div className="bg-gray-50 rounded-2xl p-6 text-center">
                <p className="text-xs text-gray-500 mb-2">累计阅读次数（会员端打开详情会调用接口递增）</p>
                <p className="text-3xl font-black text-gray-900">{(statsItem.readCount ?? 0).toLocaleString()}</p>
              </div>
              <div className="bg-amber-50 rounded-xl px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
                <i className="ri-information-line text-amber-500 flex-shrink-0 mt-0.5"></i>
                <p>
                  独立访客、停留时长、渠道与设备等维度需在接入统计分析（如小程序/公众号埋点）后提供；当前系统仅持久化后台
                  <strong className="font-semibold"> readCount </strong>字段。
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
